import { fetchComicChapters } from 'api'
import { useEffect, useMemo } from 'react'
import { useImmer } from 'use-immer'
import { SchemaComicChapterShow } from 'api/generated'

interface PageResult {
  total?: number
  current?: number
  pageSize?: number
  [key: string]: any
}

interface ComicChaptersState {
  currentOptions: { comic_id: string | undefined }
  list: SchemaComicChapterShow[]
  nextPage: number
  hasMore: boolean
  page_result: PageResult
  promise: Promise<SchemaComicChapterShow[]>
  resolve: (value: SchemaComicChapterShow[]) => void
}

type ComicChapterList = SchemaComicChapterShow[] & {
  finished: boolean
  promise: Promise<SchemaComicChapterShow[]>
}

export const useAllComicChapters = (comicId: string | number | undefined, disabled?: boolean): ComicChapterList => {
  const apiParams = {
    // WARN: keep this as a type-only cast; IDs may be int64-shaped strings and must not be coerced in JS.
    comic_id: comicId as string | undefined,
  }

  const [state, updateState] = useImmer<ComicChaptersState>(() => {
    let resolve!: (value: SchemaComicChapterShow[]) => void
    const promise = new Promise<SchemaComicChapterShow[]>((r) => {
      resolve = r
    })
    return {
      currentOptions: apiParams,
      list: [],
      nextPage: 1,
      hasMore: true,
      page_result: {},
      promise,
      resolve,
    }
  })

  useEffect(() => {
    const fetcherController = new AbortController()

    if (disabled) {
      return
    }

    if (!state.hasMore) {
      state.resolve(state.list)
      return
    }

    const data = {
      signal: fetcherController.signal,
      current: state.nextPage,
      pageSize: 100,
      // WARN: force cast string to number because js do not support int64.
      ...(state.currentOptions as unknown as { comic_id: number }),
    }

    fetchComicChapters(data).then((response: any) => {
      updateState((draft) => {
        draft.list = draft.list.concat(response?.data)
        draft.hasMore = response?.page_result?.total > response?.page_result?.current * response?.page_result?.pageSize
        draft.nextPage = response?.page_result?.current + 1
        draft.page_result = response.page_result
      })
    })

    return () => {
      fetcherController.abort()
    }
  }, [disabled, state, state.currentOptions, state.hasMore, state.nextPage, updateState])

  const mappedList = useMemo(() => {
    return Object.assign([...state.list], { finished: false, promise: state.promise }) as ComicChapterList
  }, [state.list, state.promise])

  return mappedList
}
